import {
    applyMutationsToGraphJson,
    type EntityCatalogEntry,
    type MutationDescriptor,
} from './graph-mutations';

export const STALE_LIVE_EDITOR_ERROR_CODE = 'stale-live-editor';

export class StaleLiveEditorError extends Error {
    constructor(message: string) {
        super(`${STALE_LIVE_EDITOR_ERROR_CODE}: ${message}`);
        this.name = 'StaleLiveEditorError';
    }
}

type HeadlessMutationRequest = {
    graphJson: string;
    mutations: MutationDescriptor[];
    entityCatalog: EntityCatalogEntry[];
};

export type HeadlessGraphMutation = (request: HeadlessMutationRequest) => Promise<string>;

export type LiveGraphSnapshot = {
    generation: number;
    graphJson: string;
};

export type ApplyLiveGraphMutationOptions = {
    initialSnapshot: LiveGraphSnapshot;
    mutations: MutationDescriptor[];
    entityCatalog: EntityCatalogEntry[];
    getGeneration: () => number;
    captureFreshSnapshot: () => LiveGraphSnapshot;
    commit: (graphJson: string) => void;
    operationIsCurrent?: () => boolean;
    applyHeadless?: HeadlessGraphMutation;
};

export type LiveGraphMutationResult = { applied: boolean; abandoned: boolean };

/**
 * Applies one descriptor batch to a live editor snapshot. A single re-apply is allowed when the
 * editor changes during headless work, and the commit callback is invoked synchronously after the
 * final generation check so no live event can interleave between validation and replacement.
 */
export async function applyLiveGraphMutation(options: ApplyLiveGraphMutationOptions): Promise<LiveGraphMutationResult> {
    const applyHeadless = options.applyHeadless ?? applyMutationsToGraphJson;
    const mutationRequest = (graphJson: string, mutations: MutationDescriptor[]): HeadlessMutationRequest => ({
        graphJson,
        mutations,
        entityCatalog: options.entityCatalog,
    });

    const firstResult = await applyHeadless(
        mutationRequest(options.initialSnapshot.graphJson, options.mutations),
    );

    if (options.operationIsCurrent && !options.operationIsCurrent()) {
        return { applied: false, abandoned: true };
    }

    if (options.getGeneration() === options.initialSnapshot.generation) {
        options.commit(firstResult);
        return { applied: true, abandoned: false };
    }

    const freshSnapshot = options.captureFreshSnapshot();
    const [normalizedInitial, normalizedFresh] = await Promise.all([
        applyHeadless(mutationRequest(options.initialSnapshot.graphJson, [])),
        applyHeadless(mutationRequest(freshSnapshot.graphJson, [])),
    ]);

    if (options.operationIsCurrent && !options.operationIsCurrent()) {
        return { applied: false, abandoned: true };
    }

    const writtenIds = deriveWrittenCellIds(normalizedInitial, firstResult);
    if (!writtenCellsAreUnchanged(normalizedInitial, normalizedFresh, writtenIds)) {
        throw new StaleLiveEditorError('A cell changed in both the live editor and the requested mutation. Re-inspect and retry.');
    }

    // Re-run the descriptors from scratch against the fresh graph. The first result is never used
    // as an input to the retry.
    const rebasedResult = await applyHeadless(
        mutationRequest(freshSnapshot.graphJson, options.mutations),
    );

    if (options.operationIsCurrent && !options.operationIsCurrent()) {
        return { applied: false, abandoned: true };
    }

    if (options.getGeneration() !== freshSnapshot.generation) {
        throw new StaleLiveEditorError('The live editor changed again while the mutation was being re-applied. Re-inspect and retry.');
    }

    options.commit(rebasedResult);
    return { applied: true, abandoned: false };
}

export function deriveWrittenCellIds(normalizedInitialJson: string, mutatedJson: string): Set<string> {
    const initialCells = cellsById(normalizedInitialJson);
    const mutatedCells = cellsById(mutatedJson);
    const allIds = new Set([...initialCells.keys(), ...mutatedCells.keys()]);
    const writtenIds = new Set<string>();

    for (const id of allIds) {
        if (!jsonValuesEqual(initialCells.get(id), mutatedCells.get(id))) {
            writtenIds.add(id);
        }
    }

    return writtenIds;
}

export function writtenCellsAreUnchanged(
    normalizedInitialJson: string,
    normalizedFreshJson: string,
    writtenIds: ReadonlySet<string>,
): boolean {
    const initialCells = cellsById(normalizedInitialJson);
    const freshCells = cellsById(normalizedFreshJson);

    for (const id of writtenIds) {
        // Map#get returns undefined for absence, which deliberately makes creation conflict when
        // the id is now occupied and removal conflict when the user already deleted the target.
        if (!jsonValuesEqual(initialCells.get(id), freshCells.get(id))) {
            return false;
        }
    }

    return true;
}

function cellsById(graphJson: string): Map<string, unknown> {
    const parsed = JSON.parse(graphJson) as { cells?: unknown };
    if (!Array.isArray(parsed.cells)) {
        throw new Error('Graph JSON must contain a cells array.');
    }

    const cells = new Map<string, unknown>();
    for (const value of parsed.cells) {
        if (value === null || typeof value !== 'object') {
            throw new Error('Every graph cell must be an object.');
        }

        const id = (value as { id?: unknown }).id;
        if (typeof id !== 'string' && typeof id !== 'number') {
            throw new Error('Every graph cell must have a string or numeric id.');
        }

        const key = String(id);
        if (cells.has(key)) {
            throw new Error(`Graph JSON contains duplicate cell id '${key}'.`);
        }
        cells.set(key, value);
    }

    return cells;
}

function jsonValuesEqual(left: unknown, right: unknown): boolean {
    if (Object.is(left, right)) {
        return true;
    }

    if (Array.isArray(left) || Array.isArray(right)) {
        if (!Array.isArray(left) || !Array.isArray(right) || left.length !== right.length) {
            return false;
        }
        return left.every((value, index) => jsonValuesEqual(value, right[index]));
    }

    if (left !== null && right !== null && typeof left === 'object' && typeof right === 'object') {
        const leftRecord = left as Record<string, unknown>;
        const rightRecord = right as Record<string, unknown>;
        const leftKeys = Object.keys(leftRecord).sort();
        const rightKeys = Object.keys(rightRecord).sort();
        if (leftKeys.length !== rightKeys.length
            || leftKeys.some((key, index) => key !== rightKeys[index])) {
            return false;
        }
        return leftKeys.every(key => jsonValuesEqual(leftRecord[key], rightRecord[key]));
    }

    return false;
}
